<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import { Textarea } from "@/components/ui/textarea";
  import { useGridsStore } from "@stores/survey/grids/grids";

  const open = defineModel<boolean>("open", { required: true });

  const gridsStore = useGridsStore();
  const { askQuestion } = gridsStore;
  const { currentGrid } = storeToRefs(gridsStore);

  const description = computed(() =>
    currentGrid.value?.grid_type === "population"
      ? "The question is answered once per population in this grid, from all its interviews, each answer backed by quotes. A comparative summary is generated on top."
      : "The question is asked of every respondent in this grid, one answer per row, each backed by quotes from the interview.",
  );

  const prompt = ref("");
  const submitting = ref(false);

  const submit = async () => {
    if (!prompt.value.trim() || submitting.value) return;

    submitting.value = true;
    await askQuestion(prompt.value.trim());
    submitting.value = false;
    open.value = false;
  };

  watch(open, (isOpen) => {
    if (isOpen) prompt.value = "";
  });
</script>

<template>
  <FormDialog
    :open="open"
    title="Ask AI question"
    confirm-label="Ask"
    max-width="520px"
    :loading="submitting"
    :can-save="!!prompt.trim()"
    @update:open="open = $event"
    @save="submit">
    <p class="text-13 text-muted-foreground">{{ description }}</p>
    <Textarea
      v-model="prompt"
      rows="3"
      class="max-h-[50vh]"
      placeholder="How do they react to the concept?"
      @keydown.meta.enter.prevent="submit"
      @keydown.ctrl.enter.prevent="submit" />
  </FormDialog>
</template>
